import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import router from '@/router';
import store from '@/store';

type PendingDeepLink = {
  path: string;
  /** payment-success и др. требуют сессию; invite — нет. */
  needsAuth: boolean;
};

let pending: PendingDeepLink | null = null;
let handlersAttached = false;
let routerReady = false;
let flushTimer: number | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

/** Разобрать deep link / universal link → внутренний путь роутера. */
export function parseDeepLinkUrl(url: string): PendingDeepLink | null {
  const raw = (url || '').trim();
  if (!raw) return null;

  try {
    if (raw.startsWith('ligateam://messenger/invite/')) {
      const token = raw.replace(/^ligateam:\/\/messenger\/invite\//, '').split(/[?#]/)[0];
      if (token) {
        return { path: `/messenger/invite/${encodeURIComponent(token)}`, needsAuth: false };
      }
    }

    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const httpsInvite = raw.match(/\/messenger\/invite\/([A-Za-z0-9_-]+)/);
      if (httpsInvite?.[1]) {
        return { path: `/messenger/invite/${encodeURIComponent(httpsInvite[1])}`, needsAuth: false };
      }
    }

    if (raw.startsWith('ligateam://payment-success')) {
      const q = raw.indexOf('?');
      const query = q >= 0 ? raw.slice(q) : '';
      const params = new URLSearchParams(query);
      const eventId = params.get('eventId');
      const purchaseId = params.get('purchaseId');
      const releaseId = params.get('releaseId');
      const subscriptionId = params.get('subscriptionId');
      if (purchaseId && releaseId) {
        return {
          path: `/events/success?releasePaid=1&purchaseId=${encodeURIComponent(purchaseId)}&releaseId=${encodeURIComponent(releaseId)}`,
          needsAuth: true,
        };
      }
      if (purchaseId && subscriptionId) {
        return {
          path: `/events/success?subscriptionPaid=1&purchaseId=${encodeURIComponent(purchaseId)}&subscriptionId=${encodeURIComponent(subscriptionId)}`,
          needsAuth: true,
        };
      }
      if (purchaseId) {
        return {
          path: `/events/success?releasePaid=1&purchaseId=${encodeURIComponent(purchaseId)}`,
          needsAuth: true,
        };
      }
      if (eventId) {
        return {
          path: `/events/success?eventId=${encodeURIComponent(eventId)}`,
          needsAuth: true,
        };
      }
      return { path: '/events/success', needsAuth: true };
    }
  } catch (error) {
    console.warn('[deep-link] parse failed', error);
  }
  return null;
}

function canNavigate(item: PendingDeepLink): boolean {
  if (!routerReady) return false;
  if (item.needsAuth && !store.getters.authenticated) return false;
  return true;
}

async function navigateToPath(path: string): Promise<void> {
  try {
    // replace: не оставляем Login/Showcase «под» целевым экраном после cold start.
    await router.replace(path);
  } catch (error) {
    console.warn('[deep-link] navigate failed', path, error);
    try {
      await router.push(path);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Сохранить URL и попытаться открыть, когда роутер/сессия готовы.
 * На cold start appUrlOpen часто срабатывает до mount — без очереди роут сбрасывается.
 */
export function queueDeepLinkUrl(url: string): void {
  const parsed = parseDeepLinkUrl(url);
  if (!parsed) return;
  pending = parsed;
  void flushPendingDeepLink();
}

/** Вызвать после router.isReady() + mount. */
export function markDeepLinkRouterReady(): void {
  routerReady = true;
  void flushPendingDeepLink();
}

/**
 * Открыть отложенный deep link (после login / restore / resume).
 * Несколько попыток: стартовый redirect на Login/Showcase иначе перетирает цель.
 */
export async function flushPendingDeepLink(): Promise<void> {
  if (!pending) return;
  if (!canNavigate(pending)) return;

  const target = pending;
  if (flushTimer != null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }

  for (const delayMs of [0, 120, 350, 800, 1600]) {
    if (!pending || pending.path !== target.path) return;
    if (!canNavigate(pending)) return;
    if (delayMs > 0) await sleep(delayMs);
    if (!pending || pending.path !== target.path) return;

    await navigateToPath(pending.path);

    const current = router.currentRoute.value?.fullPath || '';
    const want = pending.path.split('?')[0];
    if (current === pending.path || current.startsWith(want)) {
      pending = null;
      return;
    }
  }
}

export async function attachDeepLinkHandlersEarly(): Promise<void> {
  if (handlersAttached || !Capacitor.isNativePlatform()) return;
  handlersAttached = true;

  CapacitorApp.addListener('appUrlOpen', (ev: { url?: string }) => {
    if (ev?.url) queueDeepLinkUrl(ev.url);
  });

  // Cold start: URL часто только здесь, appUrlOpen может не прийти.
  try {
    const launch = await CapacitorApp.getLaunchUrl();
    if (launch?.url) {
      queueDeepLinkUrl(launch.url);
    }
  } catch (error) {
    console.warn('[deep-link] getLaunchUrl failed', error);
  }
}
